import { useMemo, useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import MusicRegistrationHeader from '@/components/Layout/MusicRegistrationHeader';
import { MUSIC_PRICE_REFERENCE, type MusicPriceRange } from '@/constants/musicPriceReference';
import { useI18n } from '@/i18n';
import type { Locale } from '@/i18n/types';

const COPY = {
  en: {
    eyebrow: 'Music lesson pricing',
    title: 'Music Lesson Rate Guide',
    intro: 'Compare typical one-to-one music lesson rates in Malaysia by instrument and learning level.',
    heading: 'Hourly rate reference',
    subheading: 'Private one-to-one lessons · Malaysian Ringgit (RM) per hour',
    searchLabel: 'Search instruments',
    searchPlaceholder: 'Search by instrument name',
    instrument: 'Instrument',
    beginner: 'Beginner / Pre-grade',
    lowerGrade: 'Lower grade / Grade 1–3',
    upperGrade: 'Upper grade / Grade 4–8',
    adultBeginner: 'Adult beginner',
    noResults: 'No matching instruments found.',
    clearSearch: 'Clear search',
    guideTitle: 'How to use this guide',
    guideBody: 'Use these ranges as a starting point when setting a tutor rate or family budget. Actual fees can vary based on qualifications, teaching experience, lesson duration, location, travel, and exam preparation.',
    noteTitle: 'Rates are for reference only',
    noteBody: 'Tutors and families should confirm the final lesson fee together before lessons begin.',
    result: (count: number) => `${count} instrument${count === 1 ? '' : 's'}`,
  },
  'zh-CN': {
    eyebrow: '音乐课程收费',
    title: '音乐课程收费参考',
    intro: '按乐器与学习程度，比较马来西亚一对一音乐课程的常见收费。',
    heading: '每小时收费参考',
    subheading: '私人一对一课程 · 马来西亚令吉（RM）／小时',
    searchLabel: '搜索乐器',
    searchPlaceholder: '输入乐器名称',
    instrument: '乐器',
    beginner: '入门／考级前',
    lowerGrade: '初级／1–3级',
    upperGrade: '中高级／4–8级',
    adultBeginner: '成人初学',
    noResults: '找不到相关乐器。',
    clearSearch: '清除搜索',
    guideTitle: '如何使用这份参考',
    guideBody: '导师可用这些区间作为定价起点，家长与学生也可用来规划预算。实际收费会因导师资历、教学经验、课程时长、地点、交通及考级准备而不同。',
    noteTitle: '以上收费仅供参考',
    noteBody: '导师与家长或学生应在课程开始前共同确认最终收费。',
    result: (count: number) => `共 ${count} 种乐器`,
  },
  ms: {
    eyebrow: 'Harga kelas muzik',
    title: 'Panduan Kadar Kelas Muzik',
    intro: 'Bandingkan kadar biasa kelas muzik satu dengan satu di Malaysia mengikut alat muzik dan tahap pembelajaran.',
    heading: 'Rujukan kadar setiap jam',
    subheading: 'Kelas persendirian satu dengan satu · Ringgit Malaysia (RM) sejam',
    searchLabel: 'Cari alat muzik',
    searchPlaceholder: 'Cari mengikut nama alat muzik',
    instrument: 'Alat muzik',
    beginner: 'Pemula / Pra-gred',
    lowerGrade: 'Gred rendah / Gred 1–3',
    upperGrade: 'Gred tinggi / Gred 4–8',
    adultBeginner: 'Dewasa pemula',
    noResults: 'Tiada alat muzik yang sepadan ditemui.',
    clearSearch: 'Kosongkan carian',
    guideTitle: 'Cara menggunakan panduan ini',
    guideBody: 'Gunakan julat ini sebagai titik permulaan untuk menetapkan kadar guru atau bajet keluarga. Kadar sebenar boleh berbeza mengikut kelayakan, pengalaman mengajar, tempoh kelas, lokasi, perjalanan dan persediaan peperiksaan.',
    noteTitle: 'Kadar ini untuk rujukan sahaja',
    noteBody: 'Guru dan keluarga hendaklah mengesahkan kadar akhir bersama sebelum kelas bermula.',
    result: (count: number) => `${count} alat muzik`,
  },
} satisfies Record<Locale, Record<string, string | ((count: number) => string)>>;

const INSTRUMENT_NAMES: Record<Locale, Record<string, string>> = {
  en: {},
  'zh-CN': {
    Piano: '钢琴',
    'Acoustic Guitar': '木吉他',
    'Electric Guitar': '电吉他',
    Violin: '小提琴',
    Flute: '长笛',
    Saxophone: '萨克斯风',
    Drums: '鼓',
    Voice: '声乐',
    Ukulele: '尤克里里',
  },
  ms: {
    Piano: 'Piano',
    'Acoustic Guitar': 'Gitar akustik',
    'Electric Guitar': 'Gitar elektrik',
    Violin: 'Violin',
    Flute: 'Seruling',
    Saxophone: 'Saksofon',
    Drums: 'Dram',
    Voice: 'Vokal',
    Ukulele: 'Ukulele',
  },
};

const formatRate = ({ min, max }: MusicPriceRange) => `RM${min}–${max}`;

const MusicPriceReferencePage = () => {
  const { locale } = useI18n();
  const copy = COPY[locale];
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const rows = useMemo(() => MUSIC_PRICE_REFERENCE.filter((item) => {
    const localizedName = INSTRUMENT_NAMES[locale][item.instrument] ?? item.instrument;
    return `${item.instrument} ${localizedName}`.toLocaleLowerCase().includes(normalizedQuery);
  }), [locale, normalizedQuery]);

  return (
    <div className="theme-music min-h-screen bg-[#fffdf9] font-redesign-body text-gray-900">
      <MusicRegistrationHeader />

      <main>
        <section className="border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-primary-100/70 px-4 py-14 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-950 sm:text-5xl">{copy.title}</h1>
          </div>
        </section>

        <section className="bg-white px-4 py-12 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-6xl">
            <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-950">{copy.heading}</h2>
                <p className="mt-2 text-sm text-gray-600">{copy.subheading}</p>
              </div>
              <div className="w-full lg:w-80">
                <label htmlFor="instrument-search" className="mb-2 block text-sm font-semibold text-gray-800">{copy.searchLabel}</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <input
                    id="instrument-search"
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={copy.searchPlaceholder}
                    className="min-h-11 w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-11 pr-4 text-base text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-600 focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
            </div>

            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-500" aria-live="polite">
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              {(copy.result as (count: number) => string)(rows.length)}
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse">
                  <thead>
                    <tr className="bg-primary text-on-primary">
                      <th scope="col" className="sticky left-0 z-[1] min-w-48 bg-primary px-5 py-4 text-left text-sm font-bold">{copy.instrument}</th>
                      <th scope="col" className="px-5 py-4 text-right text-sm font-bold">{copy.beginner}</th>
                      <th scope="col" className="px-5 py-4 text-right text-sm font-bold">{copy.lowerGrade}</th>
                      <th scope="col" className="px-5 py-4 text-right text-sm font-bold">{copy.upperGrade}</th>
                      <th scope="col" className="px-5 py-4 text-right text-sm font-bold">{copy.adultBeginner}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => (
                      <tr key={item.instrument} className="group border-t border-gray-100 transition-colors hover:bg-primary-50/60">
                        <th scope="row" className="sticky left-0 bg-white px-5 py-4 text-left text-sm font-semibold text-gray-900 group-hover:bg-primary-50/60">
                          {INSTRUMENT_NAMES[locale][item.instrument] ?? item.instrument}
                        </th>
                        <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-primary-800">{formatRate(item.beginner)}</td>
                        <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-primary-800">{formatRate(item.lowerGrade)}</td>
                        <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-primary-800">{formatRate(item.upperGrade)}</td>
                        <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-primary-800">{formatRate(item.adultBeginner)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 && (
                <div className="border-t border-gray-100 px-5 py-12 text-center">
                  <p className="text-sm font-medium text-gray-600">{copy.noResults}</p>
                  <button type="button" onClick={() => setQuery('')} className="mt-3 min-h-11 cursor-pointer rounded-lg px-4 text-sm font-semibold text-primary-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                    {copy.clearSearch}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default MusicPriceReferencePage;
